import {FC, useEffect, useState} from 'react';
//mui
import {Box, Typography} from '@mui/material';
//formik
import {useFormikContext} from 'formik';
import {AuctionFormValues} from '../../../formConfiguration';
//helpers
import {getImagePreview} from '@/utils/helpers';
//constants
import {BODY1, CANCEL, DELETE, EDIT, ERROR, FIELD, IMAGE_PREVIEW_ALT} from '@/utils/constants';
//icons
import {ReactComponent as More} from '@icons/more-vertical.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
//components
import AddDonorModalForm from '../AddDonorModalForm';
import Modal from '@/shared/components/Modal';
import Dropdown from '@/shared/components/Dropdown';
//styles
import {donorsListStyle} from './DonorsList.styles';

interface DonorsListProps {
	isModalOpen: boolean;
	onModalChange: () => void;
}

const DonorsList: FC<DonorsListProps> = ({isModalOpen, onModalChange}) => {
	const {
		setFieldValue,
		values: {donors},
	} = useFormikContext<AuctionFormValues>();

	const [isDropdownActionsOpen, setIsDropdownActionsOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const [targetDonorId, setTargetDonorId] = useState<number | null>(null);
	const [editableDonor, setEditableDonor] = useState<any | null>(null);

	useEffect(() => {
		if (!isModalOpen) setEditableDonor(null);
	}, [isModalOpen]);

	const handleDropdownButtonOpen = (id?: number | null) => () => {
		setIsDropdownActionsOpen(!isDropdownActionsOpen);
		if (id) setTargetDonorId(id);
	};

	const handleDeleteModalOpen = () => {
		setIsDeleteModalOpen(!isDeleteModalOpen);
	};

	const handleDeleteModalAccept = () => {
		const filteredDonors = donors?.filter((donor) => donor.id !== targetDonorId);
		setFieldValue(FIELD.donors, filteredDonors);
		setTargetDonorId(null);
		handleDeleteModalOpen();
	};

	const handleEditDonor = (id: number) => () => {
		const editableDonor = donors?.find((donor) => donor.id === id);
		setEditableDonor(editableDonor);
		onModalChange();
	};

	return (
		<Box sx={donorsListStyle.wrapper}>
			{donors?.map((donor) => {
				return (
					<Box sx={donorsListStyle.content} key={donor.id}>
						<Box key={donor.id} sx={donorsListStyle.donor}>
							<Box sx={donorsListStyle.donorImage}>
								<img src={getImagePreview(donor.logoUrl)} alt={IMAGE_PREVIEW_ALT} />
							</Box>

							<Box key={donor.id} sx={donorsListStyle.donorName}>
								{donor.name}
							</Box>

							<Dropdown
								parentClassName={donorsListStyle.dropdown}
								toggleContent={
									<Box
										sx={{
											...(targetDonorId === donor.id && isDropdownActionsOpen && donorsListStyle.dropdownIcon),
										}}
									>
										<More />
									</Box>
								}
								options={
									<Box>
										<Typography variant={BODY1} sx={donorsListStyle.dropdownOption} onClick={handleEditDonor(donor.id)}>
											{EDIT}
										</Typography>
										<Typography variant={BODY1} sx={donorsListStyle.dropdownOption} onClick={handleDeleteModalOpen}>
											{DELETE}
										</Typography>
									</Box>
								}
								open={targetDonorId === donor.id && isDropdownActionsOpen}
								onOpen={handleDropdownButtonOpen(donor.id)}
								onClose={handleDropdownButtonOpen()}
							/>
						</Box>
					</Box>
				);
			})}

			<Modal
				title="Delete Donor"
				description="Are you sure you want to delete donor?"
				closeButtonTitle={CANCEL}
				mainButtonTitle={DELETE}
				onClose={handleDeleteModalOpen}
				onSave={handleDeleteModalAccept}
				open={isDeleteModalOpen}
				mainIcon={<DangerIcon />}
				sx={donorsListStyle.modal}
				color={ERROR}
				isShowCloseButton
				isShowSaveButton
			/>

			{isModalOpen && <AddDonorModalForm onClose={onModalChange} editableDonor={editableDonor} isEditMode />}
		</Box>
	);
};

export default DonorsList;
